import { ALL_CANDIDATES, bit, digitsOf, lowestDigit, popcount, type Candidates, type Grid } from './types';
import { BOXES, BOX_OF, COLS, COL_OF, PEERS, ROWS, ROW_OF, UNITS } from './units';

/**
 * Menschliche Lösetechniken. Sie dienen zwei Zwecken:
 *  - Bewertung der Schwierigkeit eines Rätsels (welche Technik wird gebraucht?)
 *  - Tipps im Spiel, die einen nachvollziehbaren nächsten Schritt erklären.
 */

export type TechniqueName =
  | 'nakedSingle'
  | 'hiddenSingle'
  | 'lockedCandidates'
  | 'nakedPair'
  | 'hiddenPair'
  | 'nakedTriple'
  | 'xWing';

/** Je höher, desto anspruchsvoller die Technik. */
export const TECHNIQUE_LEVEL: Record<TechniqueName, number> = {
  nakedSingle: 1,
  hiddenSingle: 2,
  lockedCandidates: 3,
  nakedPair: 4,
  hiddenPair: 4,
  nakedTriple: 5,
  xWing: 6,
};

export const TECHNIQUE_LABEL: Record<TechniqueName, string> = {
  nakedSingle: 'Nacktes Single',
  hiddenSingle: 'Verstecktes Single',
  lockedCandidates: 'Blockbindung',
  nakedPair: 'Nacktes Paar',
  hiddenPair: 'Verstecktes Paar',
  nakedTriple: 'Nacktes Tripel',
  xWing: 'X-Wing',
};

export interface Elimination {
  index: number;
  digit: number;
}

export interface Step {
  technique: TechniqueName;
  level: number;
  /** Zelle, die direkt gesetzt werden kann. */
  placement?: { index: number; digit: number };
  /** Kandidaten, die sich ausschließen lassen. */
  eliminations: Elimination[];
  /** Zellen, die den Schritt begründen (zum Hervorheben). */
  because: number[];
  /** Erklärung in deutscher Sprache. */
  text: string;
}

export interface SolveContext {
  grid: Grid;
  candidates: Candidates;
}

const CELL_NAME = (index: number) => `R${ROW_OF[index] + 1}C${COL_OF[index] + 1}`;

function unitName(unit: Uint8Array): string {
  const first = unit[0];
  if (unit === ROWS[ROW_OF[first]]) return `Zeile ${ROW_OF[first] + 1}`;
  if (unit === COLS[COL_OF[first]]) return `Spalte ${COL_OF[first] + 1}`;
  return `Block ${BOX_OF[first] + 1}`;
}

/** Kandidatenmasken für alle leeren Zellen; belegte Zellen erhalten 0. */
export function computeCandidates(grid: Grid): Candidates {
  const cands = new Uint16Array(81);
  for (let i = 0; i < 81; i++) {
    if (grid[i] !== 0) continue;
    let mask = ALL_CANDIDATES;
    for (const p of PEERS[i]) {
      const v = grid[p];
      if (v !== 0) mask &= ~bit(v);
    }
    cands[i] = mask;
  }
  return cands;
}

export function createContext(grid: Grid): SolveContext {
  const copy = Uint8Array.from(grid);
  return { grid: copy, candidates: computeCandidates(copy) };
}

/** Wendet einen Schritt auf den Kontext an. */
export function applyStep(ctx: SolveContext, step: Step): void {
  if (step.placement) {
    const { index, digit } = step.placement;
    ctx.grid[index] = digit;
    ctx.candidates[index] = 0;
    for (const p of PEERS[index]) ctx.candidates[p] &= ~bit(digit);
  }
  for (const e of step.eliminations) {
    ctx.candidates[e.index] &= ~bit(e.digit);
  }
}

// ---------------------------------------------------------------------------
// Einzelne Techniken
// ---------------------------------------------------------------------------

function findNakedSingle(ctx: SolveContext): Step | null {
  for (let i = 0; i < 81; i++) {
    if (ctx.grid[i] !== 0) continue;
    const mask = ctx.candidates[i];
    if (popcount(mask) === 1) {
      const digit = lowestDigit(mask);
      return {
        technique: 'nakedSingle',
        level: TECHNIQUE_LEVEL.nakedSingle,
        placement: { index: i, digit },
        eliminations: [],
        because: [i],
        text: `In ${CELL_NAME(i)} ist ${digit} die einzige verbleibende Möglichkeit.`,
      };
    }
  }
  return null;
}

function findHiddenSingle(ctx: SolveContext): Step | null {
  for (const unit of UNITS) {
    for (let d = 1; d <= 9; d++) {
      const b = bit(d);
      let spot = -1;
      let count = 0;
      let alreadyPlaced = false;
      for (const i of unit) {
        if (ctx.grid[i] === d) {
          alreadyPlaced = true;
          break;
        }
        if (ctx.grid[i] === 0 && ctx.candidates[i] & b) {
          spot = i;
          count++;
        }
      }
      if (alreadyPlaced || count !== 1) continue;
      return {
        technique: 'hiddenSingle',
        level: TECHNIQUE_LEVEL.hiddenSingle,
        placement: { index: spot, digit: d },
        eliminations: [],
        because: Array.from(unit),
        text: `${d} passt in ${unitName(unit)} nur noch nach ${CELL_NAME(spot)}.`,
      };
    }
  }
  return null;
}

/** Pointing und Claiming: Ziffer ist innerhalb einer Einheit auf eine Schnittmenge beschränkt. */
function findLockedCandidates(ctx: SolveContext): Step | null {
  for (let d = 1; d <= 9; d++) {
    const b = bit(d);

    // Pointing: im Block liegen alle Kandidaten in einer Zeile/Spalte
    for (let boxIndex = 0; boxIndex < 9; boxIndex++) {
      const box = BOXES[boxIndex];
      const cells = Array.from(box).filter((i) => ctx.grid[i] === 0 && ctx.candidates[i] & b);
      if (cells.length < 2) continue;
      const sameRow = cells.every((i) => ROW_OF[i] === ROW_OF[cells[0]]);
      const sameCol = cells.every((i) => COL_OF[i] === COL_OF[cells[0]]);
      if (!sameRow && !sameCol) continue;
      const line = sameRow ? ROWS[ROW_OF[cells[0]]] : COLS[COL_OF[cells[0]]];
      const eliminations: Elimination[] = [];
      for (const i of line) {
        if (BOX_OF[i] === boxIndex) continue;
        if (ctx.grid[i] === 0 && ctx.candidates[i] & b) eliminations.push({ index: i, digit: d });
      }
      if (eliminations.length === 0) continue;
      return {
        technique: 'lockedCandidates',
        level: TECHNIQUE_LEVEL.lockedCandidates,
        eliminations,
        because: cells,
        text: `In Block ${boxIndex + 1} kann ${d} nur in ${unitName(line)} stehen – dort fällt ${d} außerhalb des Blocks weg.`,
      };
    }

    // Claiming: in der Zeile/Spalte liegen alle Kandidaten in einem Block
    for (const line of [...ROWS, ...COLS]) {
      const cells = Array.from(line).filter((i) => ctx.grid[i] === 0 && ctx.candidates[i] & b);
      if (cells.length < 2) continue;
      const boxIndex = BOX_OF[cells[0]];
      if (!cells.every((i) => BOX_OF[i] === boxIndex)) continue;
      const eliminations: Elimination[] = [];
      for (const i of BOXES[boxIndex]) {
        if (cells.includes(i)) continue;
        if (ctx.grid[i] === 0 && ctx.candidates[i] & b) eliminations.push({ index: i, digit: d });
      }
      if (eliminations.length === 0) continue;
      return {
        technique: 'lockedCandidates',
        level: TECHNIQUE_LEVEL.lockedCandidates,
        eliminations,
        because: cells,
        text: `In ${unitName(line)} kann ${d} nur in Block ${boxIndex + 1} stehen – dort fällt ${d} in den übrigen Feldern weg.`,
      };
    }
  }
  return null;
}

function findNakedSubset(ctx: SolveContext, size: 2 | 3): Step | null {
  const technique: TechniqueName = size === 2 ? 'nakedPair' : 'nakedTriple';
  for (const unit of UNITS) {
    const open = Array.from(unit).filter((i) => ctx.grid[i] === 0 && popcount(ctx.candidates[i]) > 1);
    if (open.length <= size) continue;

    const combine = (start: number, chosen: number[], mask: number): Step | null => {
      if (popcount(mask) > size) return null;
      if (chosen.length === size) {
        if (popcount(mask) !== size) return null;
        const eliminations: Elimination[] = [];
        for (const i of unit) {
          if (chosen.includes(i) || ctx.grid[i] !== 0) continue;
          for (const d of digitsOf(ctx.candidates[i] & mask)) eliminations.push({ index: i, digit: d });
        }
        if (eliminations.length === 0) return null;
        const digits = digitsOf(mask).join(', ');
        const cells = chosen.map(CELL_NAME).join(', ');
        return {
          technique,
          level: TECHNIQUE_LEVEL[technique],
          eliminations,
          because: [...chosen],
          text: `${cells} in ${unitName(unit)} teilen sich genau die Ziffern ${digits} – anderswo in der Einheit fallen sie weg.`,
        };
      }
      for (let k = start; k < open.length; k++) {
        const found = combine(k + 1, [...chosen, open[k]], mask | ctx.candidates[open[k]]);
        if (found) return found;
      }
      return null;
    };

    const step = combine(0, [], 0);
    if (step) return step;
  }
  return null;
}

function findHiddenPair(ctx: SolveContext): Step | null {
  for (const unit of UNITS) {
    const spots = new Map<number, number[]>();
    for (let d = 1; d <= 9; d++) {
      const b = bit(d);
      const cells = Array.from(unit).filter((i) => ctx.grid[i] === 0 && ctx.candidates[i] & b);
      if (cells.length === 2) spots.set(d, cells);
    }
    const digits = [...spots.keys()];
    for (let a = 0; a < digits.length; a++) {
      for (let b = a + 1; b < digits.length; b++) {
        const cellsA = spots.get(digits[a])!;
        const cellsB = spots.get(digits[b])!;
        if (cellsA[0] !== cellsB[0] || cellsA[1] !== cellsB[1]) continue;
        const keep = bit(digits[a]) | bit(digits[b]);
        const eliminations: Elimination[] = [];
        for (const i of cellsA) {
          for (const d of digitsOf(ctx.candidates[i] & ~keep)) eliminations.push({ index: i, digit: d });
        }
        if (eliminations.length === 0) continue;
        return {
          technique: 'hiddenPair',
          level: TECHNIQUE_LEVEL.hiddenPair,
          eliminations,
          because: [...cellsA],
          text: `${digits[a]} und ${digits[b]} passen in ${unitName(unit)} nur nach ${CELL_NAME(cellsA[0])} und ${CELL_NAME(cellsA[1])} – dort bleiben nur diese beiden Ziffern.`,
        };
      }
    }
  }
  return null;
}

function findXWing(ctx: SolveContext): Step | null {
  for (let d = 1; d <= 9; d++) {
    const b = bit(d);
    for (const orientation of ['row', 'col'] as const) {
      const lines = orientation === 'row' ? ROWS : COLS;
      const crossOf = orientation === 'row' ? COL_OF : ROW_OF;
      const crossLines = orientation === 'row' ? COLS : ROWS;

      const positions: { line: number; cells: number[] }[] = [];
      for (let l = 0; l < 9; l++) {
        const cells = Array.from(lines[l]).filter((i) => ctx.grid[i] === 0 && ctx.candidates[i] & b);
        if (cells.length === 2) positions.push({ line: l, cells });
      }
      for (let a = 0; a < positions.length; a++) {
        for (let c = a + 1; c < positions.length; c++) {
          const A = positions[a];
          const C = positions[c];
          const crossA = A.cells.map((i) => crossOf[i]);
          const crossC = C.cells.map((i) => crossOf[i]);
          if (crossA[0] !== crossC[0] || crossA[1] !== crossC[1]) continue;

          const eliminations: Elimination[] = [];
          for (const cross of crossA) {
            for (const i of crossLines[cross]) {
              if (A.cells.includes(i) || C.cells.includes(i)) continue;
              if (ctx.grid[i] === 0 && ctx.candidates[i] & b) eliminations.push({ index: i, digit: d });
            }
          }
          if (eliminations.length === 0) continue;
          const label = orientation === 'row' ? 'Zeilen' : 'Spalten';
          return {
            technique: 'xWing',
            level: TECHNIQUE_LEVEL.xWing,
            eliminations,
            because: [...A.cells, ...C.cells],
            text: `X-Wing: ${d} steht in den ${label} ${A.line + 1} und ${C.line + 1} jeweils nur an denselben zwei Positionen – in den kreuzenden Einheiten fällt ${d} weg.`,
          };
        }
      }
    }
  }
  return null;
}

const FINDERS: ((ctx: SolveContext) => Step | null)[] = [
  findNakedSingle,
  findHiddenSingle,
  findLockedCandidates,
  (ctx) => findNakedSubset(ctx, 2),
  findHiddenPair,
  (ctx) => findNakedSubset(ctx, 3),
  findXWing,
];

/** Nächster logisch begründbarer Schritt, von der einfachsten Technik aufwärts. */
export function findNextStep(ctx: SolveContext): Step | null {
  for (const finder of FINDERS) {
    const step = finder(ctx);
    if (step) return step;
  }
  return null;
}

/**
 * Kosten eines Schrittes je Technikstufe. Singles sind Fleißarbeit und zählen
 * kaum, jede weitere Stufe kostet deutlich mehr – so schlägt sich sowohl die
 * Art als auch die Häufigkeit anspruchsvoller Schritte in der Bewertung nieder.
 */
const STEP_COST = [0, 0, 1, 8, 14, 22, 35];

/** Aufschlag, wenn die implementierten Techniken nicht bis zur Lösung reichen. */
const UNSOLVED_PENALTY = 120;

export interface Rating {
  /** Höchste benötigte Technikstufe (0, wenn nichts zu tun war). */
  maxLevel: number;
  /** Wurde das Rätsel allein mit diesen Techniken gelöst? */
  solved: boolean;
  /** Anzahl ausgeführter Schritte. */
  steps: number;
  /** Welche Techniken kamen vor? */
  used: Set<TechniqueName>;
  /**
   * Gesamtaufwand: Anzahl der leeren Felder plus Kosten aller Schritte.
   * Je höher, desto schwerer – siehe `classify()` im Generator.
   */
  score: number;
}

/** Bewertet ein Rätsel, indem es mit menschlichen Techniken nachgespielt wird. */
export function rateGrid(grid: Grid): Rating {
  const ctx = createContext(grid);
  const used = new Set<TechniqueName>();
  let maxLevel = 0;
  let steps = 0;
  let cost = 0;

  let empty = 0;
  for (let i = 0; i < 81; i++) if (grid[i] === 0) empty++;
  // Grundaufwand: jedes leere Feld will ausgefüllt werden.
  const base = empty;

  for (;;) {
    if (empty === 0) {
      return { maxLevel, solved: true, steps, used, score: base + cost };
    }

    const step = findNextStep(ctx);
    if (!step) {
      return { maxLevel, solved: false, steps, used, score: base + cost + UNSOLVED_PENALTY };
    }

    applyStep(ctx, step);
    if (step.placement) empty--;
    used.add(step.technique);
    maxLevel = Math.max(maxLevel, step.level);
    cost += STEP_COST[step.level] ?? step.level * 6;
    steps++;
  }
}
